import React, { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  Bus,
  Calendar,
  Camera,
  ChevronDown,
  Check,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  FileText,
  GraduationCap,
  IndianRupee,
  Library,
  LogOut,
  Menu,
  MessageSquare,
  Package,
  Plus,
  Receipt,
  Search,
  ShieldCheck,
  Send,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { sendPasswordResetEmail, signOut } from "firebase/auth";
import { auth } from "./config/firebase";
import { attendanceApi, authApi, faceAttendanceApi, leaveApi, schoolApi } from "./services/api";

const icons = {
  overview: BarChart3,
  attendance: UserCheck,
  fees: IndianRupee,
  staff: Users,
  results: GraduationCap,
  notices: Bell,
  branches: Building2,
  finance: Wallet,
  classes: Calendar,
  homework: ClipboardList,
  marks: CheckCircle2,
  syllabus: BookOpen,
  messages: MessageSquare,
  leave: FileText,
  timetable: Calendar,
  children: Users,
  reportcard: FileText,
  transport: Bus,
  study: Library,
  tests: CheckCircle2,
  collections: Wallet,
  admissions: UserPlus,
  inventory: Package,
  payroll: CreditCard,
  idcards: Receipt,
  visitors: Users,
  tasks: ClipboardList,
  documents: FileText,
  "self-attendance": Camera,
};
const configs = {
  director: {
    name: "Dr. Rajesh Sharma",
    role: "Principal",
    greeting: "Good morning, Dr. Sharma",
    subtitle: "Here’s what’s happening across your school today.",
    tabs: [
      ["overview", "Overview"],
      ["fees", "Fee Collection"],
      ["staff", "Staff & Leave"],
      ["results", "Result Analytics"],
      ["notices", "Circulars"],
      ["branches", "Branches"],
      ["finance", "Financial Reports"],
    ],
    stats: [
      ["Total Students", "1,842", "+36 this session", "indigo"],
      ["Fees Collected", "₹42.8L", "78% of ₹55L", "emerald"],
      ["Student Attendance", "94.2%", "1,735 present", "amber"],
      ["Staff Attendance", "91.6%", "109 of 119", "sky"],
    ],
    actions: [
      ["Broadcast notice", Send, "notices"],
      ["Review fee dues", Receipt, "fees"],
      ["Approve leaves", CheckCircle2, "staff"],
      ["View analytics", TrendingUp, "results"],
    ],
  },
  teacher: {
    name: "Anita Verma",
    role: "Class Teacher · VIII-A",
    greeting: "Good morning, Anita",
    subtitle: "You have 5 classes and 2 pending reviews today.",
    tabs: [
      ["overview", "Overview"],
      ["attendance", "Attendance"],
      ["homework", "Homework"],
      ["marks", "Marks Entry"],
      ["syllabus", "Lesson Plan"],
      ["messages", "Parent Messages"],
      ["leave", "My Leave"],
      ["timetable", "Timetable"],
    ],
    stats: [
      ["Classes Today", "5", "Next: VIII-A · 10:20", "indigo"],
      ["My Students", "42", "VIII-A class teacher", "sky"],
      ["Attendance", "95.2%", "2 students absent", "emerald"],
      ["To Review", "18", "2 assignments", "amber"],
    ],
    actions: [
      ["Take attendance", UserCheck, "attendance"],
      ["Add homework", Plus, "homework"],
      ["Enter marks", CheckCircle2, "marks"],
      ["Message parent", MessageSquare, "messages"],
    ],
  },
  parent: {
    name: "Priya Mehta",
    role: "Parent · Aarav Mehta",
    greeting: "Hello, Priya",
    subtitle: "Aarav is present today and has 2 upcoming assignments.",
    tabs: [
      ["overview", "Overview"],
      ["attendance", "Attendance"],
      ["fees", "Fee Payment"],
      ["homework", "Homework & Exams"],
      ["reportcard", "Report Cards"],
      ["messages", "Teacher Chat"],
      ["transport", "Bus Tracking"],
      ["leave", "Child Leave"],
    ],
    stats: [
      ["Attendance", "96.4%", "Present today", "emerald"],
      ["Fee Due", "₹8,500", "Due 25 Aug", "amber"],
      ["Homework", "2", "Due this week", "indigo"],
      ["Class Rank", "8 / 42", "Up by 3 places", "sky"],
    ],
    actions: [
      ["Pay fees", CreditCard, "fees"],
      ["View homework", BookOpen, "homework"],
      ["Message teacher", MessageSquare, "messages"],
      ["Apply leave", FileText, "leave"],
    ],
  },
  student: {
    name: "Aarav Mehta",
    role: "Student · Class VIII-A",
    greeting: "Hey Aarav, ready to learn?",
    subtitle: "Your next class is Mathematics at 10:20 AM.",
    tabs: [
      ["overview", "Overview"],
      ["attendance", "My Attendance"],
      ["homework", "Assignments"],
      ["timetable", "Timetable"],
      ["results", "Results"],
      ["study", "Study Material"],
      ["tests", "Tests & Quizzes"],
    ],
    stats: [
      ["Attendance", "96.4%", "Excellent", "emerald"],
      ["Assignments", "2", "Due this week", "amber"],
      ["Average Score", "84.6%", "+4.2% this term", "indigo"],
      ["Next Exam", "12 days", "Mathematics", "sky"],
    ],
    actions: [
      ["View homework", ClipboardList, "homework"],
      ["Open timetable", Calendar, "timetable"],
      ["Study material", Library, "study"],
      ["Practice quiz", CheckCircle2, "tests"],
    ],
  },
  accountant: {
    name: "Suresh Kumar",
    role: "Accounts Officer",
    greeting: "Good morning, Suresh",
    subtitle: "Today’s collection is ₹1.24L across 38 receipts.",
    tabs: [
      ["overview", "Overview"],
      ["collections", "Fee Receipts"],
      ["fees", "Pending Dues"],
      ["finance", "Income & Expense"],
      ["payroll", "Payroll"],
      ["admissions", "Admissions"],
      ["inventory", "Inventory"],
      ["idcards", "ID Cards"],
      ["visitors", "Visitors"],
    ],
    stats: [
      ["Collected Today", "₹1.24L", "38 receipts", "emerald"],
      ["This Month", "₹12.6L", "84% of target", "indigo"],
      ["Pending Dues", "₹8.7L", "126 students", "amber"],
      ["Expenses", "₹3.42L", "This month", "sky"],
    ],
    actions: [
      ["New receipt", Receipt, "collections"],
      ["Fee defaulters", AlertTriangle, "fees"],
      ["Add admission", UserPlus, "admissions"],
      ["Run payroll", CreditCard, "payroll"],
    ],
  },
  staff: {
    name: "Mohan Singh",
    role: "Office Staff",
    greeting: "Good morning, Mohan",
    subtitle: "You have 4 assigned tasks, including 2 due today.",
    tabs: [
      ["overview", "Overview"],
      ["tasks", "My Tasks"],
      ["attendance", "Attendance"],
      ["admissions", "Admissions"],
      ["inventory", "Inventory"],
      ["visitors", "Visitors"],
      ["documents", "Documents"],
      ["leave", "My Leave"],
    ],
    stats: [
      ["Open Tasks", "4", "2 due today", "amber"],
      ["Completed", "18", "This month", "emerald"],
      ["Attendance", "97.1%", "Present today", "indigo"],
      ["Visitors Today", "12", "3 currently inside", "sky"],
    ],
    actions: [
      ["Add visitor", UserPlus, "visitors"],
      ["New admission", GraduationCap, "admissions"],
      ["Update inventory", Package, "inventory"],
      ["Apply leave", FileText, "leave"],
    ],
  },
};
configs.parent.tabs = configs.parent.tabs.filter(
  ([value]) => value !== "leave",
);
configs.teacher.tabs.push(
  ["self-attendance", "My Face Attendance"],
  ["documents", "My Documents"],
);
configs.staff.tabs.splice(configs.staff.tabs.findIndex(([value]) => value === "documents"), 0, ["self-attendance", "My Face Attendance"]);
configs.accountant.tabs.push(["self-attendance", "My Face Attendance"], ["documents", "My Documents"]);
configs.student.role = "Student";
configs.parent.role = "Parent";
configs.teacher.role = "Teacher";
configs.parent.actions = configs.parent.actions.filter(
  ([, , value]) => value !== "leave",
);
Object.values(configs).forEach((config) => {
  config.name = config.role;
  config.greeting = `Welcome to ${config.role} dashboard`;
  config.subtitle = "Your live school records will appear here.";
  config.stats = config.stats.map(([label, value, note, color]) => [
    label,
    value.includes("₹") ? "₹0" : value.includes("%") ? "—" : "0",
    "No records yet",
    color,
  ]);
});
const colors = {
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  sky: "bg-sky-500",
};
const schedule = [];
function Card({ children, className = "" }) {
  return (
    <section
      className={`rounded-2xl border border-slate-800 bg-slate-900 ${className}`}
    >
      {children}
    </section>
  );
}
function Head({ title, action }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
      <h3 className="font-bold text-white">{title}</h3>
      {action && (
        <span className="text-xs font-semibold text-indigo-400">{action}</span>
      )}
    </div>
  );
}
function Status({ children, kind = "green" }) {
  const c =
    kind === "red"
      ? "bg-red-500/10 text-red-400"
      : kind === "amber"
        ? "bg-amber-500/10 text-amber-400"
        : "bg-emerald-500/10 text-emerald-400";
  return (
    <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${c}`}>
      {children}
    </span>
  );
}
function Performance() {
  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center p-6 text-center">
      <BarChart3 className="h-10 w-10 text-slate-700" />
      <p className="mt-3 text-sm font-bold text-slate-300">
        No performance data yet
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Analytics will appear after marks are entered.
      </p>
    </div>
  );
}
function Overview({ config, role, go, schoolSlug }) {
  const [teacherStats, setTeacherStats] = useState(null);
  useEffect(() => {
    if (role !== "teacher") return;
    let live = true;
    (async () => {
      try {
        const profile = await authApi.profile();
        const [className, section] = (profile.class_teacher_of || "").split(" · ");
        if (!className || !section) { if (live) setTeacherStats({ assigned: false }); return; }
        const students = await schoolApi.students(schoolSlug);
        const classStudents = (Array.isArray(students) ? students : []).filter(student => student.class_name === className && student.section === section);
        const today = new Date().toISOString().slice(0, 10);
        const records = await attendanceApi.list({ attendanceDate: today, className, section });
        const present = records.filter(record => String(record.status).toLowerCase() === "present").length;
        if (live) setTeacherStats({ assigned: true, label: `${className}-${section}`, total: classStudents.length, present, absent: Math.max(classStudents.length - present, 0) });
      } catch (_) { if (live) setTeacherStats({ assigned: false }); }
    })();
    return () => { live = false; };
  }, [role, schoolSlug]);
  const stats = role === "teacher" && teacherStats ? (teacherStats.assigned ? [["My students", String(teacherStats.total), `${teacherStats.label} class`, "sky"], ["Present today", String(teacherStats.present), `of ${teacherStats.total} students`, "emerald"], ["Absent today", String(teacherStats.absent), `of ${teacherStats.total} students`, "amber"], ["Class attendance", teacherStats.total ? `${Math.round((teacherStats.present / teacherStats.total) * 100)}%` : "—", teacherStats.label, "indigo"]] : [["My students", "—", "Choose your class in My profile", "sky"], ["Present today", "—", "No class assigned", "emerald"], ["Absent today", "—", "No class assigned", "amber"], ["Class attendance", "—", "No class assigned", "indigo"]]) : config.stats;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {stats.map(([l, v, n, c]) => (
          <Card key={l} className="relative overflow-hidden p-4 sm:p-5">
            <div className={`absolute inset-y-0 left-0 w-1 ${colors[c]}`} />
            <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs">
              {l}
            </p>
            <p className="mt-2 text-2xl font-black text-white sm:text-3xl">
              {v}
            </p>
            <p className="mt-2 truncate text-[11px] text-slate-400">{n}</p>
          </Card>
        ))}
      </div>
      <div className="mt-5">
        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-500">
          Quick actions
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {config.actions.map(([l, Icon, t]) => (
            <button
              key={l}
              onClick={() => go(t)}
              className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-4 text-left hover:border-indigo-500"
            >
              <span className="rounded-xl bg-indigo-500/10 p-2.5 text-indigo-400">
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-xs font-bold text-slate-200 sm:text-sm">
                {l}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 grid gap-5 xl:grid-cols-[1.35fr_.8fr]">
        <Card>
          <Head
            title={
              role === "director" ? "School performance" : "Today’s schedule"
            }
            action="View all →"
          />
          {role === "director" ? (
            <Performance />
          ) : (
            <div className="divide-y divide-slate-800">
              {schedule.map((r, i) => (
                <div key={r[0]} className="flex items-center gap-4 px-5 py-4">
                  <b className="w-12 text-xs text-slate-400">{r[0]}</b>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">
                      {r[1]}
                    </p>
                    <p className="text-xs text-slate-500">{r[2]}</p>
                  </div>
                  <Status kind={i === 1 ? "amber" : "green"}>{r[3]}</Status>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <Head title="Needs your attention" />
          <div className="space-y-3 p-4">
            {[
              ["Fee reminders pending", "126 accounts"],
              ["Leave requests", "3 awaiting action"],
              ["New school circular", "Annual sports day"],
            ].map(([a, b]) => (
              <div
                key={a}
                className="flex items-center gap-3 rounded-xl bg-slate-950/60 p-3"
              >
                <Bell className="h-4 w-4 text-amber-400" />
                <span className="flex-1">
                  <b className="block text-xs text-white">{a}</b>
                  <small className="text-slate-500">{b}</small>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

const tables = {
  fees: [
    "Fee collection & pending dues",
    ["Student", "Class", "Amount", "Due date", "Status"],
    [
      ["Riya Sharma", "VIII-A", "₹8,500", "20 Aug", "Overdue"],
      ["Kabir Singh", "VII-B", "₹12,000", "25 Aug", "Pending"],
      ["Tanya Gupta", "IX-A", "₹8,500", "28 Aug", "Pending"],
      ["Dev Patel", "VI-C", "₹6,750", "Paid today", "Paid"],
    ],
  ],
  attendance: [
    "Digital attendance",
    ["Name", "Roll no.", "Class", "Today", "Monthly"],
    [
      ["Aarav Mehta", "08", "VIII-A", "Present", "96.4%"],
      ["Riya Sharma", "12", "VIII-A", "Present", "94.1%"],
      ["Kabir Singh", "19", "VIII-A", "Absent", "88.6%"],
      ["Tanya Gupta", "24", "VIII-A", "Present", "97.2%"],
    ],
  ],
  homework: [
    "Homework & assignments",
    ["Assignment", "Class / Subject", "Due", "Submissions", "Status"],
    [
      ["Algebra Worksheet", "VIII-A · Maths", "20 Aug", "34 / 42", "Open"],
      ["Light & Reflection", "VIII-A · Science", "22 Aug", "18 / 42", "Open"],
      ["Essay: My India", "VII-B · English", "18 Aug", "39 / 40", "Review"],
    ],
  ],
  collections: [
    "Fee receipts",
    ["Receipt", "Student", "Mode", "Amount", "Time"],
    [
      ["RC-20481", "Dev Patel", "UPI", "₹6,750", "10:42 AM"],
      ["RC-20480", "Meera Jain", "Card", "₹8,500", "10:18 AM"],
      ["RC-20479", "Arjun Rao", "Cash", "₹12,000", "09:54 AM"],
    ],
  ],
  admissions: [
    "Admission applications",
    ["Applicant", "Applying for", "Parent", "Submitted", "Status"],
    [
      ["Ishaan Shah", "Class VI", "Nitin Shah", "Today", "Documents due"],
      ["Aditi Rao", "Class III", "Kavita Rao", "Yesterday", "Verified"],
      ["Reyansh Jain", "Nursery", "Mohit Jain", "16 Aug", "New"],
    ],
  ],
  inventory: [
    "Inventory & library",
    ["Item", "Category", "Available", "Issued", "Status"],
    [
      ["NCERT Mathematics VIII", "Books", "42", "38", "Low stock"],
      ["School Uniform · M", "Uniform", "64", "—", "In stock"],
      ["Microscope", "Lab equipment", "12", "8", "Available"],
    ],
  ],
};
const fallback = [
  "Workspace records",
  ["Item", "Details", "Date", "Progress", "Status"],
  [
    [
      "Annual Sports Day",
      "Circular shared with parents",
      "22 Aug",
      "1,624 reached",
      "Active",
    ],
    [
      "Syllabus plan",
      "Class VIII · Term 1",
      "31 Aug",
      "78% complete",
      "On track",
    ],
    [
      "Leave application",
      "Personal leave",
      "24 Aug",
      "Principal review",
      "Pending",
    ],
    ["Monthly report", "Generated automatically", "Aug 2026", "Ready", "View"],
  ],
];
function StudentAttendanceHistoryPanel() {
  const [month, setMonth] = useState(new Date().toLocaleDateString("en-CA").slice(0, 7));
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    setLoading(true); setError("");
    attendanceApi.myStudents(month).then(next => { setItems(next); setSelectedId(current => next.some(item => item.student.id === current) ? current : next[0]?.student.id || ""); }).catch(e => setError(e.message || "Unable to load attendance history.")).finally(() => setLoading(false));
  }, [month]);
  const data = items.find(item => item.student.id === selectedId) || items[0];
  if (loading) return <Card className="p-10 text-center text-sm text-slate-400">Loading attendance history…</Card>;
  if (error) return <Card className="border-red-900 p-5 text-sm text-red-200">{error}</Card>;
  if (!data) return <Card className="p-10 text-center text-sm text-slate-400">No linked student profile was found. Ask the school administrator to link this login to the student.</Card>;
  const { student, summary, records } = data;
  return <div className="space-y-5"><Card className="overflow-hidden"><div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-slate-900 p-6"><div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-center gap-4"><img src={student.photograph || `https://ui-avatars.com/api/?background=4f46e5&color=fff&name=${encodeURIComponent(student.name)}`} alt="" className="h-16 w-16 rounded-2xl object-cover"/><div><h2 className="text-xl font-black">{student.name}</h2><p className="mt-1 text-xs text-indigo-300">{student.admission_number} · Class {student.class_name}-{student.section}</p></div></div><div className="flex gap-2">{items.length > 1 && <select value={selectedId} onChange={e => setSelectedId(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-xs">{items.map(item => <option key={item.student.id} value={item.student.id}>{item.student.name}</option>)}</select>}<input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-xs"/></div></div></div><div className="grid grid-cols-2 divide-x divide-slate-800 border-t border-slate-800 sm:grid-cols-4"><div className="p-5"><p className="text-xs text-slate-500">Attendance</p><b className="mt-1 block text-2xl text-indigo-300">{summary.percentage == null ? "—" : `${summary.percentage}%`}</b></div><div className="p-5"><p className="text-xs text-slate-500">Recorded days</p><b className="mt-1 block text-2xl">{summary.recorded_days}</b></div><div className="p-5"><p className="text-xs text-slate-500">Present</p><b className="mt-1 block text-2xl text-emerald-300">{summary.present}</b></div><div className="p-5"><p className="text-xs text-slate-500">Absent</p><b className="mt-1 block text-2xl text-red-300">{summary.absent}</b></div></div></Card><Card><div className="border-b border-slate-800 p-5"><h3 className="font-bold">Daily attendance</h3><p className="mt-1 text-xs text-slate-500">Every recorded school day for the selected month.</p></div><div className="divide-y divide-slate-800">{records.map(record => <div key={record.date} className="flex items-center justify-between px-5 py-4"><div><b className="text-sm">{new Date(`${record.date}T00:00:00`).toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" })}</b><p className="mt-1 text-xs text-slate-500">{record.date}</p></div><Status kind={record.status === "Present" ? "green" : "red"}>{record.status}</Status></div>)}{!records.length && <p className="p-10 text-center text-sm text-slate-500">No attendance has been recorded for this month.</p>}</div></Card></div>;
}

function TeacherAttendancePanel({ schoolSlug }) {
  const today = new Date().toLocaleDateString("en-CA");
  const [profile, setProfile] = useState(null);
  const attendanceDate = today;
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const parseAssignment = value => {
    const parts = String(value || "").replace(/[·–—]/g, "-").split("-").map(part => part.trim()).filter(Boolean);
    return parts.length >= 2 ? { className: parts.slice(0, -1).join("-"), section: parts.at(-1) } : null;
  };
  const load = async classValue => {
    setLoading(true); setError(""); setMessage("");
    try {
      const chosen = parseAssignment(classValue);
      if (!chosen) { setRows([]); return; }
      setRows(await attendanceApi.list({ attendanceDate, ...chosen }));
    } catch (e) { setRows([]); setError(e.message || "Unable to load class attendance."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    Promise.all([authApi.profile(), schoolApi.students(schoolSlug)]).then(([nextProfile, students]) => {
      setProfile(nextProfile);
      const options = [...new Set((Array.isArray(students) ? students : []).map(student => `${student.class_name} · ${student.section}`))];
      setClassOptions(options);
      const assigned = nextProfile.class_teacher_of || "";
      setSelectedClass(options.includes(assigned) ? assigned : assigned || options[0] || "");
      if (!assigned && !options.length) { setLoading(false); setError("No student classes are available for attendance."); }
    }).catch(e => { setLoading(false); setError(e.message || "Unable to load classes."); });
  }, [schoolSlug]);
  useEffect(() => { if (selectedClass) load(selectedClass); }, [selectedClass]);
  const setStatus = (studentId, status) => setRows(current => current.map(row => row.student_id === studentId ? { ...row, status } : row));
  const markAll = status => setRows(current => current.map(row => ({ ...row, status })));
  const save = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      await attendanceApi.save(attendanceDate, rows.map(row => ({ student_id: row.student_id, status: row.status })));
      setMessage(`Attendance saved for ${rows.length} student${rows.length === 1 ? "" : "s"}.`);
    } catch (e) { setError(e.message || "Unable to save attendance."); }
    finally { setSaving(false); }
  };
  const assignment = parseAssignment(selectedClass);
  const present = rows.filter(row => row.status === "Present").length;
  return <Card><div className="flex flex-col gap-4 border-b border-slate-800 p-5 sm:flex-row sm:items-center sm:justify-between"><div><h3 className="text-lg font-bold">Class attendance</h3><p className="mt-1 text-xs text-slate-500">{assignment ? (selectedClass === profile?.class_teacher_of ? `Your assigned class: ${assignment.className} · Section ${assignment.section}` : `Covering class: ${assignment.className} · Section ${assignment.section}`) : "Select a class to view its students."}</p></div><div className="flex flex-wrap gap-2"><label className="text-xs text-slate-400">Class and section<select value={selectedClass} onChange={event => setSelectedClass(event.target.value)} className="ml-2 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white">{classOptions.map(option => <option key={option} value={option}>{option}{option === profile?.class_teacher_of ? " (My class)" : ""}</option>)}</select></label><span className="rounded-xl border border-slate-800 px-3 py-2 text-xs text-slate-400">Today · {new Date().toLocaleDateString()}</span><button onClick={save} disabled={saving || loading || !rows.length} className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold disabled:opacity-50">{saving ? "Saving…" : "Save attendance"}</button></div></div>{error && <p role="alert" className="m-5 rounded-xl border border-red-800 bg-red-950/50 p-4 text-sm text-red-200">{error}</p>}{message && <p className="m-5 rounded-xl border border-emerald-800 bg-emerald-950/50 p-4 text-sm text-emerald-200">{message}</p>}{assignment && <div className="flex flex-col gap-3 border-b border-slate-800 p-4 sm:flex-row sm:items-center sm:justify-between"><p className="text-xs text-slate-400"><b className="text-white">{rows.length}</b> students · <b className="text-emerald-300">{present}</b> present · <b className="text-red-300">{rows.length - present}</b> absent</p><div className="flex gap-2"><button onClick={() => markAll("Present")} disabled={!rows.length} className="rounded-lg border border-emerald-700 px-3 py-2 text-xs font-bold text-emerald-300 disabled:opacity-40">Mark all present</button><button onClick={() => markAll("Absent")} disabled={!rows.length} className="rounded-lg border border-red-800 px-3 py-2 text-xs font-bold text-red-300 disabled:opacity-40">Mark all absent</button></div></div>}<div className="overflow-x-auto"><table className="w-full min-w-[620px] text-left"><thead className="bg-slate-950/40 text-[10px] uppercase text-slate-500"><tr><th className="px-5 py-3">Student</th><th className="px-5 py-3">Roll no.</th><th className="px-5 py-3">Class</th><th className="px-5 py-3">Attendance</th></tr></thead><tbody className="divide-y divide-slate-800">{rows.map(row => <tr key={row.student_id}><td className="px-5 py-4"><p className="text-xs font-bold text-white">{row.name}</p><p className="mt-1 text-[10px] text-slate-500">Admission: {row.admission_number || "—"}{row.gender ? ` · ${row.gender}` : ""}</p></td><td className="px-5 py-4 text-xs text-slate-400">{row.roll_number || "—"}</td><td className="px-5 py-4 text-xs text-slate-400">{assignment?.className}-{assignment?.section}</td><td className="px-5 py-4"><div className="inline-flex rounded-xl border border-slate-700 bg-slate-950 p-1"><button onClick={() => setStatus(row.student_id, "Present")} className={`rounded-lg px-3 py-2 text-xs font-bold ${row.status === "Present" ? "bg-emerald-600 text-white" : "text-slate-400"}`}>Present</button><button onClick={() => setStatus(row.student_id, "Absent")} className={`rounded-lg px-3 py-2 text-xs font-bold ${row.status === "Absent" ? "bg-red-600 text-white" : "text-slate-400"}`}>Absent</button></div></td></tr>)}{!loading && assignment && !rows.length && <tr><td colSpan="4" className="p-10 text-center text-sm text-slate-500">No students are enrolled in this class and section.</td></tr>}{loading && <tr><td colSpan="4" className="p-10 text-center text-sm text-slate-500">Loading your students…</td></tr>}</tbody></table></div></Card>;
}

function DataPanel({ tab, notify }) {
  const [title, cols, rows] = tables[tab] || fallback;
  return (
    <Card>
      <div className="flex flex-col gap-3 border-b border-slate-800 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-bold">{title}</h3>
          <p className="mt-1 text-xs text-slate-500">
            Current academic session records
          </p>
        </div>
        <button
          onClick={() => notify(`New ${title.toLowerCase()} action started`)}
          className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-bold"
        >
          <Plus className="h-4 w-4" /> Add new
        </button>
      </div>
      <div className="flex gap-3 border-b border-slate-800 p-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
          <input
            className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2 pl-9 pr-3 text-xs"
            placeholder="Search records..."
          />
        </div>
        <button className="rounded-xl border border-slate-700 px-4 text-xs font-bold">
          Filter
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-left">
          <thead className="bg-slate-950/40 text-[10px] uppercase text-slate-500">
            <tr>
              {cols.map((c) => (
                <th key={c} className="px-5 py-3">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="prototype-rows divide-y divide-slate-800">
            {rows.map((r, i) => (
              <tr key={i}>
                {r.map((v, j) => (
                  <td
                    key={j}
                    className={`px-5 py-4 text-xs ${j === 0 ? "font-bold text-white" : "text-slate-400"}`}
                  >
                    {j === r.length - 1 ? (
                      <Status
                        kind={
                          /overdue|absent|low|due/i.test(v)
                            ? "red"
                            : /pending|open|review|new/i.test(v)
                              ? "amber"
                              : "green"
                        }
                      >
                        {v}
                      </Status>
                    ) : (
                      v
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MyDocumentsPanel() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { authApi.profile().then(setProfile).catch(e => setError(e.message || "Unable to load documents.")); }, []);
  if (error) return <Card className="border-red-900 p-5 text-sm text-red-200">{error}</Card>;
  if (!profile) return <Card className="p-8 text-sm text-slate-400">Loading your documents...</Card>;
  return <div className="space-y-5"><Card className="p-5"><h3 className="text-lg font-bold">Profile photo</h3>{profile.photograph?<div className="mt-4 flex items-center gap-4"><img src={profile.photograph} alt="Profile" className="h-24 w-24 rounded-2xl object-cover"/><a href={profile.photograph} target="_blank" rel="noreferrer" className="rounded-xl border border-indigo-700 px-4 py-2 text-sm font-semibold text-indigo-200">View uploaded photo</a></div>:<p className="mt-3 text-sm text-slate-500">No profile photo uploaded.</p>}</Card><Card><div className="border-b border-slate-800 p-5"><h3 className="text-lg font-bold">My uploaded documents</h3><p className="mt-1 text-xs text-slate-500">Files uploaded by you, your school administrator, or principal.</p></div>{profile.documents?.length?<div className="divide-y divide-slate-800">{profile.documents.map(document=><div key={document.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between"><div className="flex min-w-0 items-center gap-3"><span className="rounded-xl bg-indigo-950 p-3 text-indigo-300"><FileText className="h-5 w-5"/></span><div className="min-w-0"><p className="truncate text-sm font-bold">{document.name || document.label || "Document"}</p><p className="mt-1 text-xs text-slate-500">{document.label || "Uploaded document"}</p></div></div><a href={document.url} target="_blank" rel="noreferrer" className="rounded-xl border border-indigo-700 px-4 py-2 text-center text-xs font-bold text-indigo-200 hover:bg-indigo-950">View document</a></div>)}</div>:<p className="p-8 text-center text-sm text-slate-500">No documents have been uploaded yet.</p>}</Card></div>;
}

function LeavePanel({ management = false }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ leave_type: "Casual", starts_at: "", ends_at: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = () => leaveApi.list().then(setItems).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); setBusy(true); setError(""); try { await leaveApi.create({ ...form, starts_at: new Date(form.starts_at).toISOString(), ends_at: new Date(form.ends_at).toISOString() }); setForm({ leave_type: "Casual", starts_at: "", ends_at: "", reason: "" }); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const review = async (id, status) => { setBusy(true); setError(""); try { await leaveApi.review(id, status); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  return <div className="grid gap-5 xl:grid-cols-[.8fr_1.4fr]">
    {!management && <Card className="p-5"><h3 className="text-lg font-bold">Apply for leave</h3><p className="mt-1 text-xs text-slate-500">Submit a request to your Principal.</p><form onSubmit={submit} className="mt-5 space-y-4"><select value={form.leave_type} onChange={e=>setForm({...form,leave_type:e.target.value})} className="w-full rounded-xl border border-slate-700 bg-slate-950 p-3"><option>Casual</option><option>Sick</option><option>Earned</option></select><div className="grid grid-cols-2 gap-3"><input required type="date" value={form.starts_at} onChange={e=>setForm({...form,starts_at:e.target.value})} className="rounded-xl border border-slate-700 bg-slate-950 p-3"/><input required type="date" value={form.ends_at} onChange={e=>setForm({...form,ends_at:e.target.value})} className="rounded-xl border border-slate-700 bg-slate-950 p-3"/></div><textarea required minLength="3" value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} placeholder="Reason" className="w-full rounded-xl border border-slate-700 bg-slate-950 p-3"/><button disabled={busy} className="w-full rounded-xl bg-indigo-600 py-3 font-bold disabled:opacity-50">{busy?"Submitting...":"Submit request"}</button></form></Card>}
    <Card className={management?"xl:col-span-2":""}><Head title={management?"Teacher & staff leave approvals":"My leave requests"}/>{error&&<p className="m-4 rounded-xl bg-red-950 p-3 text-sm text-red-300">{error}</p>}<div className="divide-y divide-slate-800">{items.map(item=><div key={item.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center"><div className="flex-1"><p className="font-bold">{management&&`${item.employee_name} · `}{item.leave_type}</p><p className="mt-1 text-xs text-slate-400">{new Date(item.starts_at).toLocaleDateString()} – {new Date(item.ends_at).toLocaleDateString()} · {item.reason}</p>{item.review_note&&<p className="mt-1 text-xs text-slate-500">Note: {item.review_note}</p>}</div><Status kind={item.status==="rejected"?"red":item.status==="pending"?"amber":"green"}>{item.status}</Status>{management&&item.status==="pending"&&<div className="flex gap-2"><button disabled={busy} onClick={()=>review(item.id,"approved")} className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold">Approve</button><button disabled={busy} onClick={()=>review(item.id,"rejected")} className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold">Reject</button></div>}</div>)}{!items.length&&<div className="p-10 text-center text-sm text-slate-500">No leave requests yet.</div>}</div></Card>
  </div>;
}

function FaceAttendancePanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [action, setAction] = useState("check-in");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const load = async () => {
    setError("");
    try { setData(await faceAttendanceApi.dashboard()); }
    catch (e) { setError(e.message || "Unable to load face attendance."); }
  };
  useEffect(() => { load(); }, []);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
    setCameraOpen(false);
  };
  useEffect(() => () => streamRef.current?.getTracks().forEach(track => track.stop()), []);

  const openCamera = async (nextAction) => {
    setAction(nextAction); setError(""); setMessage(""); setCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setCameraReady(true);
    } catch (_) {
      setCameraOpen(false);
      setError("Camera permission is required. Allow camera access in your browser and try again.");
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    setBusy(true); setError(""); setMessage("");
    try {
      const canvas = document.createElement("canvas");
      const size = Math.min(video.videoWidth, video.videoHeight);
      canvas.width = 720; canvas.height = 720;
      const x = (video.videoWidth - size) / 2, y = (video.videoHeight - size) / 2;
      canvas.getContext("2d").drawImage(video, x, y, size, size, 0, 0, 720, 720);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) throw new Error("Could not capture the camera image.");
      const verification = await faceAttendanceApi.verify(blob, action);
      if (!verification?.matched || !verification?.verification_token) throw new Error(verification?.message || "Face did not match your registered photo.");
      await faceAttendanceApi.mark(action, verification.verification_token);
      stopCamera();
      setMessage(action === "check-in" ? "Check-in recorded successfully." : "Check-out recorded successfully.");
      await load();
    } catch (e) { setError(e.message || "Face verification failed. Please try again."); }
    finally { setBusy(false); }
  };

  const today = data?.today || {};
  const history = data?.history || [];
  const clock = value => value ? new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--";
  const date = value => new Date(`${value}T00:00:00`).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  const canCheckIn = data?.reference_registered && !today.check_in_at;
  const canCheckOut = data?.reference_registered && !!today.check_in_at && !today.check_out_at;

  return <div className="space-y-5">
    {error && <div role="alert" className="rounded-xl border border-red-800 bg-red-950/60 p-4 text-sm text-red-200">{error}</div>}
    {message && <div className="rounded-xl border border-emerald-800 bg-emerald-950/50 p-4 text-sm text-emerald-200">{message}</div>}
    {!data && !error && <Card className="p-8 text-sm text-slate-400">Loading your attendance...</Card>}
    {data && !data.reference_registered && <div className="rounded-2xl border border-amber-700 bg-amber-950/30 p-5">
      <div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-400"/><div><h3 className="font-bold text-amber-100">Face photo is not registered</h3><p className="mt-1 text-sm text-amber-200/70">Ask your school administrator to upload and verify your face-attendance photo in your employee documents. Attendance stays disabled until registration is complete.</p></div></div>
    </div>}
    {data && <Card className="overflow-hidden">
      <div className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[.85fr_1.15fr] lg:items-center">
        <div><p className="text-xs font-bold uppercase tracking-widest text-indigo-400">Today</p><h2 className="mt-2 text-3xl font-black">{new Date().toLocaleDateString([], { weekday: "long" })}</h2><p className="mt-1 text-sm text-slate-400">{new Date().toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })}</p><p className="mt-5 text-sm text-slate-400">Office attendance · {data.timezone || "Asia/Kolkata"}</p></div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3"><div className="rounded-xl border border-emerald-800 bg-emerald-950/30 p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Checked in</p><p className="mt-1 text-xl font-black">{clock(today.check_in_at)}</p></div><div className="rounded-xl border border-indigo-800 bg-indigo-950/30 p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">Checked out</p><p className="mt-1 text-xl font-black">{clock(today.check_out_at)}</p></div></div>
          {canCheckIn && <button onClick={() => openCamera("check-in")} className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-bold hover:bg-emerald-500"><Camera className="h-5 w-5"/> Verify Face & Check In</button>}
          {canCheckOut && <button onClick={() => openCamera("check-out")} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold hover:bg-indigo-500"><Camera className="h-5 w-5"/> Verify Face & Check Out</button>}
          {today.check_out_at && <div className="rounded-xl bg-emerald-950/40 p-3 text-center text-sm font-bold text-emerald-300"><CheckCircle2 className="mr-2 inline h-5 w-5"/>Today’s attendance is complete</div>}
        </div>
      </div>
    </Card>}
    {data && <Card><Head title="My recent days"/><div className="divide-y divide-slate-800">{history.map(row => <div key={row.attendance_date} className="grid grid-cols-[1fr_auto] items-center gap-4 p-5 sm:grid-cols-4"><div><b className="text-sm">{date(row.attendance_date)}</b><p className="mt-1 text-xs text-slate-500 sm:hidden">{clock(row.check_in_at)} – {clock(row.check_out_at)}</p></div><span className="hidden text-sm text-slate-400 sm:block">In {clock(row.check_in_at)}</span><span className="hidden text-sm text-slate-400 sm:block">Out {clock(row.check_out_at)}</span><Status kind={row.status === "present" ? "green" : row.status === "late" ? "amber" : "red"}>{row.status}</Status></div>)}{!history.length && <div className="p-10 text-center text-sm text-slate-500">No face-attendance records yet.</div>}</div></Card>}
    {cameraOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"><div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl"><div className="flex items-center justify-between border-b border-slate-800 p-4"><div><b>Verify your face</b><p className="text-xs text-slate-400">Look straight at the camera in good light.</p></div><button onClick={stopCamera} disabled={busy} aria-label="Close camera"><X/></button></div><div className="relative aspect-square overflow-hidden bg-black"><video ref={videoRef} playsInline muted className="h-full w-full -scale-x-100 object-cover"/><div className="pointer-events-none absolute inset-[13%] rounded-[45%] border-2 border-dashed border-white/80"/></div><div className="p-4"><button onClick={capture} disabled={!cameraReady || busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold disabled:opacity-50"><Camera className="h-5 w-5"/>{busy ? "Verifying securely..." : `Capture & ${action === "check-in" ? "Check In" : "Check Out"}`}</button><p className="mt-3 text-center text-xs text-slate-500">Your face is compared securely on the server. A match is required.</p></div></div></div>}
  </div>;
}

function ProfilePageView({ role, profile, form, setForm, classOptions, error, message, busy, save, uploadPhoto, resetPassword }) {
  if (!profile) return <div className="mx-auto max-w-5xl"><p className="text-xs font-bold uppercase tracking-widest text-indigo-400">Account</p><h1 className="mt-1 text-3xl font-black">My profile</h1>{error?<p role="alert" className="mt-6 rounded-xl border border-red-800 bg-red-950/60 p-4 text-sm text-red-200">{error}</p>:<p className="mt-6 text-sm text-slate-400">Loading your profile…</p>}</div>;
  return <form onSubmit={save} className="mx-auto max-w-5xl"><div className="flex flex-col gap-4 border-b border-slate-700 pb-6 sm:flex-row sm:items-end sm:justify-between"><div><p className="text-xs font-bold uppercase tracking-widest text-indigo-400">Account</p><h1 className="mt-1 text-3xl font-black">My profile</h1><p className="mt-2 text-sm text-slate-400">Manage your personal details, photo and sign-in security.</p></div><button disabled={busy} className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-bold disabled:opacity-50">{busy?"Saving…":"Save profile"}</button></div>{error&&<p role="alert" className="mt-5 rounded-xl border border-red-800 bg-red-950/60 p-4 text-sm text-red-200">{error}</p>}{message&&<p className="mt-5 rounded-xl border border-emerald-800 bg-emerald-950/50 p-4 text-sm text-emerald-200">{message}</p>}<div className="grid gap-8 py-8 lg:grid-cols-[220px_1fr]"><section className="flex flex-col items-center border-b border-slate-700 pb-8 text-center lg:border-b-0 lg:border-r lg:pr-8"><div>{profile.photograph?<img src={profile.photograph} alt="Profile" className="h-32 w-32 rounded-full border-4 border-indigo-500 object-cover"/>:<span className="flex h-32 w-32 items-center justify-center rounded-full bg-indigo-600 text-4xl font-black">{(profile.username||"U").slice(0,1).toUpperCase()}</span>}</div><b className="mt-4 text-lg">{profile.username}</b><p className="mt-1 text-xs capitalize text-slate-400">{profile.role}</p><label className="mt-5 cursor-pointer rounded-xl border border-indigo-500 px-4 py-2 text-xs font-bold text-indigo-200 hover:bg-indigo-950">Upload new photo<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={busy}/></label></section><section><h2 className="mb-5 text-lg font-bold">Personal details</h2><div className="grid gap-5 sm:grid-cols-2"><label className="text-sm">Name<input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3" required/></label><label className="text-sm">Email<input value={profile.email||""} readOnly className="mt-2 w-full cursor-not-allowed rounded-xl border border-slate-800 bg-slate-950 p-3 text-slate-400"/></label><label className="text-sm">Phone<input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"/></label><label className="text-sm">Role<input value={profile.role||""} readOnly className="mt-2 w-full cursor-not-allowed rounded-xl border border-slate-800 bg-slate-950 p-3 capitalize text-slate-400"/></label>{role==="teacher"&&<label className="text-sm sm:col-span-2">Class teacher of<select value={form.class_teacher_of} onChange={e=>setForm({...form,class_teacher_of:e.target.value})} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"><option value="">Not assigned</option>{classOptions.map(option=><option key={option} value={option}>{option}</option>)}</select></label>}<label className="text-sm sm:col-span-2">Address<textarea value={form.address} onChange={e=>setForm({...form,address:e.target.value})} className="mt-2 min-h-28 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"/></label></div><div className="mt-8 flex flex-wrap gap-3 border-t border-slate-700 pt-6"><button disabled={busy} className="rounded-xl bg-indigo-600 px-6 py-3 text-sm font-bold disabled:opacity-50">Save profile</button><button type="button" disabled={busy} onClick={resetPassword} className="rounded-xl border border-slate-700 px-6 py-3 text-sm font-bold hover:bg-slate-800 disabled:opacity-50">Send password reset email</button></div></section></div></form>;
}

function UniversalProfilePanel({ role, schoolSlug, onProfileChange }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ username: "", phone: "", address: "", class_teacher_of: "" });
  const [classOptions, setClassOptions] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadProfile = async () => {
    setError("");
    try {
      const next = await authApi.profile();
      setProfile(next);
      setForm({ username: next.username || "", phone: next.phone || "", address: next.address || "", class_teacher_of: next.class_teacher_of || "" });
    } catch (e) {
      setError(e.message || "Unable to load your profile.");
    }
  };
  useEffect(() => { loadProfile(); }, []);
  useEffect(() => { if (role === "teacher") schoolApi.students(schoolSlug).then(rows => setClassOptions([...new Set((Array.isArray(rows) ? rows : []).map(row => `${row.class_name} · ${row.section}`))])).catch(() => {}); }, [role, schoolSlug]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setMessage(""); setError("");
    try {
      const next = await authApi.updateProfile(form);
      setProfile(next);
      onProfileChange?.(next);
      setMessage("Your profile has been saved.");
    } catch (e) { setError(e.message || "Unable to save your profile."); }
    finally { setBusy(false); }
  };
  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true); setMessage(""); setError("");
    try {
      const next = await authApi.uploadProfilePhoto(file);
      setProfile(next);
      onProfileChange?.(next);
      setMessage("Profile photo updated.");
    } catch (e) { setError(e.message || "Unable to upload the profile photo."); }
    finally { setBusy(false); event.target.value = ""; }
  };
  const resetPassword = async () => {
    if (!profile?.email) return;
    setBusy(true); setMessage(""); setError("");
    try {
      await sendPasswordResetEmail(auth, profile.email);
      setMessage(`A password-reset link was sent to ${profile.email}.`);
    } catch (e) { setError(e.message || "Unable to send the password reset email."); }
    finally { setBusy(false); }
  };
  const photo = profile?.photograph;
  return <ProfilePageView {...{role,profile,form,setForm,classOptions,error,message,busy,save,uploadPhoto,resetPassword}}/>;
  return <div className="mx-auto max-w-4xl">
    <p className="text-xs font-bold uppercase tracking-widest text-indigo-400">Account</p>
    <h1 className="mt-1 text-3xl font-black">My profile</h1>
    <p className="mt-2 text-sm text-slate-400">Manage your personal details, profile photo and sign-in security.</p>
    {error && <p className="mt-5 rounded-xl border border-red-800 bg-red-950/60 p-3 text-sm text-red-200">{error}</p>}
    {message && <p className="mt-5 rounded-xl border border-emerald-800 bg-emerald-950/50 p-3 text-sm text-emerald-200">{message}</p>}
    {!profile ? <Card className="mt-6 p-8 text-sm text-slate-400">Loading your profile…</Card> : <form onSubmit={save} className="mt-6 grid gap-5 lg:grid-cols-[280px_1fr]">
      <Card className="p-6"><div className="flex flex-col items-center text-center">{photo ? <img src={photo} alt="Profile" className="h-28 w-28 rounded-full border-4 border-indigo-500 object-cover"/> : <span className="flex h-28 w-28 items-center justify-center rounded-full bg-indigo-600 text-3xl font-black">{(profile.username || "U").slice(0, 1).toUpperCase()}</span>}<b className="mt-4 text-lg">{profile.username || "Your account"}</b><p className="mt-1 text-xs text-slate-400">{profile.role}</p><label className="mt-5 cursor-pointer rounded-xl border border-indigo-500 px-4 py-2 text-xs font-bold text-indigo-200 hover:bg-indigo-950">Change photo<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={busy}/></label></div></Card>
      <Card className="p-6"><div className="grid gap-4 sm:grid-cols-2"><label className="text-sm">Name<input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3" required/></label><label className="text-sm">Email<input value={profile.email || ""} readOnly className="mt-2 w-full cursor-not-allowed rounded-xl border border-slate-800 bg-slate-950 p-3 text-slate-400"/></label><label className="text-sm">Phone<input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"/></label><label className="text-sm">Role<input value={profile.role || ""} readOnly className="mt-2 w-full cursor-not-allowed rounded-xl border border-slate-800 bg-slate-950 p-3 capitalize text-slate-400"/></label>{role === "teacher" && <label className="text-sm sm:col-span-2">Class teacher of<select value={form.class_teacher_of} onChange={e => setForm({ ...form, class_teacher_of: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"><option value="">Not assigned</option>{classOptions.map(option => <option key={option} value={option}>{option}</option>)}</select><span className="mt-1 block text-xs text-slate-500">Choose the class and section you manage. Your overview will use these students.</span></label>}</div><label className="mt-4 block text-sm">Address<textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="mt-2 min-h-24 w-full rounded-xl border border-slate-700 bg-slate-950 p-3"/></label><div className="mt-6 flex flex-wrap gap-3"><button disabled={busy} className="rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold disabled:opacity-50">{busy ? "Saving…" : "Save profile"}</button><button type="button" disabled={busy} onClick={resetPassword} className="rounded-xl border border-slate-700 px-5 py-3 text-sm font-bold hover:bg-slate-800 disabled:opacity-50">Send password reset email</button></div></Card>
    </form>}
  </div>;
}

function RolePortal({ role, schoolSlug, navigate, managementContent = null, activeManagementRoute = null }) {
  const c = configs[role],
    [tab, setTab] = useState("overview"),
    [menu, setMenu] = useState(false),
    [toast, setToast] = useState(""),
    [profileMenu, setProfileMenu] = useState(false),
    [peopleOpen, setPeopleOpen] = useState(() => ["students", "teachers", "staff", "accountants"].includes(activeManagementRoute)),
    [headerProfile, setHeaderProfile] = useState(null),
    [notifications, setNotifications] = useState([
      { id: "welcome", title: "Workspace ready", body: "Your teacher workspace is ready to use.", time: "Just now" },
      { id: "students", title: "Student records", body: "Student profiles and attendance are available from the sidebar.", time: "Today" },
    ]);
  useEffect(() => { authApi.profile().then(setHeaderProfile).catch(() => {}); }, []);
  const go = (t) => {
      if (managementContent && t === "overview") {
        navigate(`${role}-dashboard`, schoolSlug);
        return;
      }
      setTab(t);
      setMenu(false);
    },
    notify = (m) => {
      setToast(m);
      setTimeout(() => setToast(""), 2500);
    },
    logout = async () => {
      try {
        await signOut(auth);
      } finally {
        navigate("landing");
      }
    };
  const active = c.tabs.find((x) => x[0] === tab) || c.tabs[0];
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/95">
        <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setMenu(true)} className="lg:hidden">
              <Menu />
            </button>
            <span className="rounded-xl bg-indigo-600 p-2">
              <GraduationCap />
            </span>
            <div>
              <b className="block text-sm">
                EduNexa <i className="not-italic text-amber-400">360</i>
              </b>
              <small className="text-slate-500">
                {schoolSlug || "apex"} · 2026–27
              </small>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => go("notifications")} className="relative rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white" aria-label="Open notifications"><Bell className="h-5 w-5" />{notifications.length > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400"/>}</button>
            <div className="relative"><button onClick={() => setProfileMenu(value => !value)} className="flex items-center gap-2 rounded-xl p-1.5 hover:bg-slate-800">{headerProfile?.photograph ? <img src={headerProfile.photograph} alt="Profile" className="h-8 w-8 rounded-full border border-indigo-400 object-cover"/> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-xs font-black">{c.name.slice(0, 1)}</span>}<span className="hidden text-left sm:block"><b className="block text-xs">{headerProfile?.username || c.name}</b><small className="text-slate-500">{c.role}</small></span></button>{profileMenu && <div className="absolute right-0 mt-2 w-48 rounded-xl border border-slate-700 bg-slate-900 p-2 shadow-2xl"><p className="px-3 py-2 text-xs text-slate-400">Signed in as {c.role}</p><button onClick={() => { setProfileMenu(false); go("profile"); }} className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-800">My profile</button><button onClick={logout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-300 hover:bg-red-950/50"><LogOut className="h-4 w-4"/>Sign out</button></div>}</div>
          </div>
        </div>
      </header>
      {menu && (
        <button
          onClick={() => setMenu(false)}
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
        />
      )}
      <div className="mx-auto flex max-w-[1500px]">
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-72 border-r border-slate-800 bg-slate-950 p-3 pt-5 transition lg:sticky lg:top-16 lg:z-0 lg:h-[calc(100vh-4rem)] lg:translate-x-0 ${menu ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="mb-4 flex justify-between px-3 lg:hidden">
            <b>Portal menu</b>
            <button onClick={() => setMenu(false)}>
              <X />
            </button>
          </div>
          <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-600">
            {c.role} workspace
          </p>
          <nav className="space-y-1">
            <button onClick={() => go("overview")} className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-xs font-semibold ${tab === "overview" && !activeManagementRoute ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-900"}`}><BarChart3 className="h-4 w-4" /> Overview</button>
            <button onClick={() => go("profile")} className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-xs font-semibold ${tab === "profile" ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-900"}`}><Users className="h-4 w-4" /> My profile</button>
            {role === "teacher" && <button onClick={() => navigate("students", schoolSlug)} className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-xs font-semibold ${activeManagementRoute === "students" && tab === "overview" ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-900"}`}><Users className="h-4 w-4" /> Students</button>}
            {role === "director" && <div className="rounded-xl border border-slate-800/80 bg-slate-900/30 p-1"><button onClick={() => setPeopleOpen(value => !value)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-xs font-semibold ${["students","teachers","staff","accountants"].includes(activeManagementRoute) ? "text-white" : "text-slate-400 hover:text-white"}`} aria-expanded={peopleOpen}><Users className="h-4 w-4"/><span className="flex-1 text-left">People & Administration</span><ChevronDown className={`h-4 w-4 transition-transform ${peopleOpen ? "rotate-180" : ""}`}/></button>{peopleOpen && <div className="space-y-1 border-t border-slate-800 px-1 pt-1">{[["students","Students"],["teachers","Teachers"],["staff","Staff"],["accountants","Accountants"]].map(([route,label]) => <button key={route} onClick={() => { navigate(route,schoolSlug); setMenu(false); }} className={`w-full rounded-lg px-8 py-2.5 text-left text-xs ${activeManagementRoute === route ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>{label}</button>)}</div>}</div>}
            {c.tabs.filter(([v]) => v !== "overview").map(([v, l]) => {
              const Icon = icons[v] || FileText;
              return (
                <button
                  key={v}
                  onClick={() => go(v)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-xs font-semibold ${tab === v ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-900"}`}
                >
                  <Icon className="h-4 w-4" />
                  {l}
                </button>
              );
            })}
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-4 pb-24 sm:p-6 lg:p-8">
          {tab === "profile" ? <UniversalProfilePanel role={role} schoolSlug={schoolSlug} onProfileChange={setHeaderProfile} /> : tab === "notifications" ? <div className="mx-auto max-w-3xl"><div className="mb-6 flex items-center justify-between"><div><p className="text-xs font-bold uppercase tracking-widest text-indigo-400">Updates</p><h1 className="mt-1 text-3xl font-black">Notifications</h1></div><button onClick={() => setNotifications([])} disabled={!notifications.length} className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-bold disabled:opacity-40">Clear all</button></div><div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">{notifications.map(item => <div key={item.id} className="flex gap-4 border-b border-slate-800 p-5 last:border-0"><span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-indigo-400"/><div className="flex-1"><p className="font-bold">{item.title}</p><p className="mt-1 text-sm text-slate-400">{item.body}</p><p className="mt-2 text-xs text-slate-500">{item.time}</p></div><button onClick={() => setNotifications(current => current.filter(notification => notification.id !== item.id))} className="text-xs text-slate-400 hover:text-red-300">Dismiss</button></div>)}{!notifications.length && <div className="p-12 text-center text-sm text-slate-500">You are all caught up.</div>}</div></div> : managementContent && tab === "overview" ? managementContent : <>
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-400">
                {tab === "overview" ? "Dashboard" : active[1]}
              </p>
              <h1 className="mt-1 text-2xl font-black sm:text-3xl">
                {tab === "overview" ? c.greeting : active[1]}
              </h1>
              <p className="mt-2 text-xs text-slate-400">
                {tab === "overview"
                  ? c.subtitle
                  : `Manage ${active[1].toLowerCase()} from your workspace.`}
              </p>
            </div>
            <span className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-2 text-xs text-slate-400">
              <Calendar className="mr-2 inline h-4 w-4" />
              Monday, 17 August 2026
            </span>
          </div>
          {tab === "overview" ? (
            <Overview config={c} role={role} go={go} schoolSlug={schoolSlug} />
          ) : tab === "leave" ? (
            <LeavePanel />
          ) : tab === "staff" && role === "director" ? (
            <LeavePanel management />
          ) : tab === "self-attendance" ? (
            <FaceAttendancePanel />
          ) : tab === "documents" ? (
            <MyDocumentsPanel />
          ) : tab === "attendance" && ["parent", "student"].includes(role) ? (
            <StudentAttendanceHistoryPanel />
          ) : tab === "attendance" && role === "teacher" ? (
            <TeacherAttendancePanel schoolSlug={schoolSlug} />
          ) : (
            <DataPanel tab={tab} notify={notify} />
          )}
          </>}
        </main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex overflow-x-auto border-t border-slate-800 bg-slate-950 p-2 lg:hidden">
        {c.tabs.slice(0, 5).map(([v, l]) => {
          const Icon = icons[v] || FileText;
          return (
            <button
              key={v}
              onClick={() => go(v)}
              className={`flex min-w-[70px] flex-1 flex-col items-center gap-1 rounded-lg py-2 text-[9px] ${tab === v ? "bg-indigo-600" : "text-slate-500"}`}
            >
              <Icon className="h-4 w-4" />
              {l}
            </button>
          );
        })}
      </nav>
      {toast && (
        <div className="fixed bottom-20 right-4 z-[60] flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-xs font-bold">
          <Check className="h-4 w-4" />
          {toast}
        </div>
      )}
    </div>
  );
}
export const DirectorDashboard = (p) => <RolePortal role="director" {...p} />;
export const TeacherDashboard = (p) => <RolePortal role="teacher" {...p} />;
export const StudentDashboard = (p) => <RolePortal role="student" {...p} />;
export const ParentDashboard = (p) => <RolePortal role="parent" {...p} />;
export const AccountantDashboard = (p) => (
  <RolePortal role="accountant" {...p} />
);
export const StaffDashboard = (p) => <RolePortal role="staff" {...p} />;
